import React, { useState, useEffect } from 'react';
import Styles, { Loader, useScreen } from '../../styles';
import { get, set, size, first, isObject, sortBy } from 'global/lib/lodash';
import clsx from 'clsx';
import { exportToCsv, getAvg, uppercaseFirst } from '../../utils';
import { getHeatchartScore } from '../../utils/getDisplayScore';
import moment from 'moment';
import { Button, Icon, Typography, FormControlLabel, Switch } from '@material-ui/core';
import { ColorSlider } from '../color-slider';
import useDebounce from '../../hooks/useDebounce';
import { getColorFromScore } from '../../styles';
import useStyles from './styles';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
// import Switch from 'global/components/switch';

const filterAvgItems = (items, returnValue = true) => {
  return items.filter((item) => !!item && typeof get(item, 'value') !== 'string' && get(item, 'value') !== -1).map((v) => (returnValue ? get(v, 'value') : v));
};

const avgItems = (items = []) => {
  return getAvg(filterAvgItems(items));
};

export const sumCount = (items) => {
  return (items || []).reduce((acc, c) => acc + get(c, 'count', 0), 0);
};

export const HeatChart = (props) => {
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });
  const styles = Styles();
  const { data, loading, rows, columns, headerRow, chips } = props;
  const [processing, setProcessing] = useState(false);
  const ranges = getAccountSetting(SETTINGS.assessmentColorScale);
  const [slide, setSlide] = useState(ranges || [0, 100]);
  const [state, setState] = useState({ showCounts: false });
  const slideDebounce = useDebounce(slide, 500);

  const [chart, setChart] = useState({
    title: '',
    cols: [],
    tables: [], //array of array of rows
    header: [],
    includeRowAverage: false,
    includeColAverage: false,
    error: undefined,
  });

  const hasData = !!size(data) && !!rows && !!columns && !loading;

  const exportTo = (format = 'csv') => {
    try {
      setProcessing(true);

      const expRows = [['Title', ...chart.header.map((h) => getHeaderTitle({ val: h, ...state }))]];

      chart.tables.map((tabl, i) => {
        expRows.push([tabl.title]);

        if (chart.includeColAverage) {
          expRows.push([
            headerAvgTitle(tabl, state.showCounts),
            ...get(chart, `cols.${i}`, []).map((col, _i) => {
              const ck = colKey(tabl);
              const foundHeaderRow = findHeaderRow(ck, col);
              const headerRowItems = _i === 0 ? findHeaderRow(ck, get(chart, `cols.${i}`), true) || [] : [];
              const value = ck
                ? _i === 0
                  ? avgItems(headerRowItems.map((hr) => ({ value: parseFloat(get(hr, 'calculated_value', 0)) })))
                  : parseFloat(get(foundHeaderRow, 'calculated_value', -1))
                : avgItems(col);
              const count = ck ? (_i === 0 ? headerRowItems.reduce((a, c) => a + get(c, 'count', 0), 0) : get(foundHeaderRow, 'count', 0)) : sumCount(col);
              return getDisplayVal({ ...col, val: { value, config: get(col, '0.config'), count } }).display;
            }),
          ]);
        }

        get(tabl, `rows`, [])
          .map((col) => col.map((c) => getDisplayVal({ ...c, val: c }).display))
          .map((r) => expRows.push(r));
      });

      const fileName = (() => {
        return `DataExport-${new Date().toISOString()}.csv`;
      })();

      exportToCsv(fileName, expRows);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const getDisplayVal = (row) => {
    let { val } = row;
    const { value, config, tooltip, count } = row.val;

    let isVal = !!isObject(val);
    const hasClick = get(row, 'val.onClick');
    let isLink = false;
    let scoreVal = val;
    let display = val;

    if (typeof value === 'string' || typeof value === 'undefined') {
      if (hasClick) {
        isLink = true;
      }
      isVal = false;
      if (rows === 'checkpoint' && tooltip) {
        display = `${value}: ${tooltip}`;
      } else {
        display = value || 'None';
      }
    } else {
      if (value === -1) {
        isVal = true;
      }
      scoreVal = value;
      display = getHeatchartScore(row.val, config || { max_score: 1, min_score: 0 });
    }

    if (state.showCounts && count !== undefined) {
      display = count;
    }

    return {
      isVal,
      isLink,
      hasClick,
      scoreVal,
      display,
      slide,
    };
  };

  const getHeaderTitle = (row) => {
    let { val, showCounts } = row;
    const id = get(row, 'val.id');
    const colKey = get(row, 'val.colKey', '');
    const isString = typeof id === 'string';

    if (id && isString && moment(id).isValid()) {
      const format = colKey.endsWith('year') ? 'YYYY' : colKey.endsWith('quarter') ? 'MMM YYYY' : 'L';
      val = moment(id).format(format);
    }
    if (showCounts && val === 'Average') {
      return 'Total';
    }
    return val;
  };

  useEffect(() => {
    if (loading) {
      setChart((c) => ({ ...c, error: undefined }));
    }
  }, [loading]);

  const isDate = ['yearly', 'quarterly', 'monthly'].indexOf(columns) > -1;

  const getSelectedColumnGroupByColumn = () => {
    let gb = get(props, 'selectedColumn.groupBy');
    if (typeof gb === 'function') {
      return gb(props);
    }
    return gb;
  };

  useEffect(() => {
    if (hasData && !loading) {
      if (data.error) {
        return setChart({ ...chart, error: data.error });
      }

      let colArr = [];
      const tableMap = {};
      const includeColAverage = get(props, 'selectedColumn.showColAvg', false) || get(props, 'selectedRow.showColAvg', false);

      let colKey = isDate ? columns.replace('ly', '') : columns;

      const getCol = (d, setRow = false) => {
        const { month, year } = d;
        const groupBy = getSelectedColumnGroupByColumn() || colKey;

        if (isDate && year) {
          const dateKey = columns.replace('ly', '');
          colKey = `relationships.${dateKey}`;
          if (setRow) {
            let _month = parseInt(month) - 1; //months start at 0 in moment
            _month = isNaN(_month) ? 1 : _month;
            const tme = dateKey === 'quarter' ? moment().month(_month).year(year).startOf('month').toISOString() : moment().month(_month).year(year).startOf(dateKey).toISOString();
            set(d, `relationships.${dateKey}.id`, tme);
          }
        } else {
          colKey = `relationships.${groupBy}`;
        }

        return get(d, colKey);
      };

      const getRow = (d) => {
        const groupBy = get(props, 'selectedRow.groupBy', rows);
        return get(d, `relationships.${groupBy}`);
      };

      const getTableId = (d) => {
        let tableBy = get(props, 'selectedRow.tableBy');
        if (typeof tableBy === 'function') {
          tableBy = tableBy(props);
        }
        return tableBy ? get(d, `relationships.${tableBy}.id`, 0) : 0;
      };

      const getTableRow = (d) => {
        let tableBy = get(props, 'selectedRow.tableBy');
        if (typeof tableBy === 'function') {
          tableBy = tableBy(props);
        }
        return get(d, `relationships.${tableBy}`);
      };

      data.map((d) => {
        const row = getRow(d);
        const col = getCol(d, true);

        const rowid = get(row, 'id', 0);
        const colid = get(col, 'id', col);
        const tableid = getTableId(d);

        if (colid !== undefined && !colArr.find((c) => get(c, 'id') === colid)) {
          const name = get(col, 'name', colid || 'none');
          colArr.push({ ...col, name, colKey });
        }

        set(tableMap, [tableid, rowid], [...get(tableMap, [tableid, rowid], []), d]);
      });

      colArr = colArr.sort((a, b) => {
        const isDate = moment(a.id).isValid();

        if (isDate) {
          return moment(a.id) - moment(b.id);
        } else {
          return a.id - b.id;
        }
      });

      const cols = {}; //map of col averages
      const tablerows = {};

      const sortRows = (_rows) => {
        const hasOrder = _rows.find((r) => r.find((rr) => rr.order !== undefined));
        if (hasOrder) {
          return sortBy(_rows, (o) =>
            get(
              (o || []).find((oo) => oo.order !== undefined),
              'order'
            )
          );
        } else {
          return sortBy(_rows, (o) => get(o, '0.value'));
        }
      };

      Object.keys(tableMap).map((k, _i) => {
        const arr = get(tableMap, k, []).filter((a) => a);

        const tabrows = arr.map((ar) => {
          const group = getRow(ar[0]);
          const title = get(group, 'name');
          const tooltip = get(group, 'description');

          const rowcalcs = colArr.map((c, i) => {
            i = includeColAverage ? i + 1 : i;
            const hasVal = ar.find((a) => get(getCol(a), 'id', getCol(a)) === get(c, 'id'));
            const calc = hasVal ? parseFloat(get(hasVal, 'calculated_value', 0)) : 'N/A';
            const count = get(hasVal, 'count');
            //get our column averages
            const configKey = 'relationships.edition.config';
            const ob = {
              value: calc,
              count,
              id: get(c, 'id'),
              order: get(hasVal, 'order'),
              config: get(
                hasVal,
                configKey,
                get(
                  ar.find((a) => get(a, configKey)),
                  configKey
                )
              ),
              relationships: get(hasVal, 'relationships'),
            };
            set(cols, [_i, i], [...get(cols, [_i, i], []), ob]);
            return ob;
          });

          const titleClick = (() => {
            const { tier } = get(props, 'selectedRow', {});
            const edition = get(ar, '0.relationships.edition');
            if (tier === 'scorecard' || tier === 'superset') {
              return () => {
                let obj = { [`${rows}_id`]: { id: get(group, 'id'), name: title } };

                if (!!edition) {
                  obj = {
                    ...obj,
                    edition_id: { id: get(edition, 'id'), name: get(edition, 'name') },
                  };
                }

                props.onRowSelect(obj);
              };
            }
          })();

          const rowAvg = {
            rowcalcs,
            value: avgItems(rowcalcs),
            count: sumCount(rowcalcs),
            config: get(
              rowcalcs.find((r) => !!r.config),
              'config'
            ),
          };
          if (includeColAverage) {
            set(cols, [_i, 0], [...get(cols, [_i, 0], []), rowAvg]);
          }

          return [{ value: title, tooltip, onClick: titleClick }, ...[rowAvg], ...rowcalcs];
        });
        const rr = getTableRow(first(first(arr)));
        set(tablerows, _i, {
          title: get(rr, 'name'),
          rows: sortRows(tabrows),
        });
      });

      setChart({
        ...chart,
        colKey,
        cols,
        header: [...['Average'], ...colArr],
        tables: Object.keys(tablerows).map((k) => tablerows[k]),
        includeColAverage,
      });
    }
  }, [data, rows, columns, loading, slideDebounce, headerRow]);

  if (chart.error) {
    return (
      <div className={clsx(classes.loadBuffer)}>{data.error}</div>
    );
  }

  if (!size(data) && !!rows && !!columns && !loading) {
    return (
      <div className={classes.loadBuffer}>{'No results, try updating the filters to include more data.'}</div>
    );
  }

  const colKey = (tab) => {
    const gb = get(props, 'selectedColumn.groupBy');
    if (!!size(headerRow) && chart.includeColAverage && gb) {
      let ck = get(props, 'selectedColumn.colKey', gb);
      if (typeof ck === 'function') {
        ck = ck(props);
      }
      const useRelId = size(chart.tables) > 1 ? true : false;
      return { useRelId, id: `${ck}_id` };
    }
    return null;
  };
  const headerAvgTitle = (tab, showCounts = false) => {
    if (size(chart.tables) > 1) {
      return get(tab, 'title');
    }
    let selRowId = get(props, 'selectedRow.parent');
    if (selRowId === 'scorecard_header') {
      selRowId = 'scorecard';
    }

    let keyy = get(chips, `${selRowId}_id.name`, selRowId || (showCounts ? 'Total' : 'Average'));
    return uppercaseFirst(keyy);
  };

  const findHeaderRow = (ck, val, returnAll = false) => {
    if (!ck || !val || !Array.isArray(headerRow)) {
      return;
    }

    const { id, useRelId } = ck;
    const [key, _id] = id.split('_');
    const valId = returnAll
      ? val.reduce((a, c) => a || c.reduce((ac, cc) => ac || get(cc, useRelId ? `relationships.${key}.id` : 'id'), null), null)
      : val.reduce((a, c) => a || get(c, useRelId ? `relationships.${key}.id` : 'id'), null);
    const filtered = useRelId ? headerRow.filter((hr) => get(hr, id) == valId) : headerRow;
    if (returnAll) {
      return filtered;
    }
    return filtered.find((hr) => get(hr, `${get(props, 'selectedColumn.groupBy', 'realm')}${isDate ? '' : '_id'}`) == get(val, '0.id'));
  };

  const changeCounts = () => {
    setState((s) => ({ ...s, showCounts: !s.showCounts }));
  };

  return (
    <>
      {hasData && !loading && (
        <>
          <div className={clsx(classes.slideContainer, classes.hidePrint)}>
            <Typography variant={isMobile ? "caption" : "subtitle1"} color={isMobile ? "textSecondary" : undefined}>Color Scale</Typography>

            <div className={classes.slideWrap}>
              <ColorSlider value={slide} onChange={(e, v) => setSlide(v)} />
            </div>
          </div>
          <FormControlLabel
            className={clsx(classes.switch, classes.hidePrint)}
            control={<Switch checked={!!state.showCounts} onChange={changeCounts} />}
            label={state.showCounts ? 'Hide observation counts' : 'Show observation counts'}
          />
          <Button
            className={clsx(classes.exportButton, classes.hidePrint)}
            startIcon={processing ? <Loader /> : <Icon>save_alt</Icon>}
            onClick={() => exportTo()}
            variant={'contained'}
            color={'primary'}
          >
            {`DOWNLOAD CHART DATA`}
          </Button>
        </>
      )}
      <div className={clsx(classes.container, classes.rowAverage)}>
        {loading && (
          <div className={clsx(styles.centerColumn, classes.loadBuffer)}>
            <Loader size={30} />
          </div>
        )}

        {hasData &&
          !loading &&
          get(chart, 'tables', []).map((tab, i) => (
            <table key={i} className={classes.chartContainer}>
              {i === 0 && (
                <thead className={'headercells'}>
                  <tr className={classes.row}>
                    <td className={classes.col_0}></td>
                    {chart.header.map((h, i) => (
                      <HeaderCell key={i} val={h} getHeaderTitle={getHeaderTitle} {...state} />
                    ))}
                  </tr>
                </thead>
              )}
              <thead className="headerrow">
                <tr className={clsx(classes.row, classes.notopborder)}>
                  <td className={clsx(classes.col, classes.col_0)}>{headerAvgTitle(tab, state.showCounts)}</td>
                  {get(chart, `cols.${i}`, []).map((val, _i) => {
                    const ck = colKey(tab);
                    const foundHeaderRow = findHeaderRow(ck, val);
                    const headerRowItems = _i === 0 ? findHeaderRow(ck, get(chart, `cols.${i}`), true) || [] : [];
                    const value = ck
                      ? _i === 0
                        ? avgItems(headerRowItems.map((hr) => ({ value: parseFloat(get(hr, 'calculated_value', 0)) })))
                        : parseFloat(get(foundHeaderRow, 'calculated_value'))
                      : avgItems(val);
                    const count = ck ? (_i === 0 ? headerRowItems.reduce((a, c) => a + get(c, 'count', 0), 0) : get(foundHeaderRow, 'count', 0)) : sumCount(val);
                    return (
                      <Cell
                        key={_i}
                        getDisplayVal={getDisplayVal}
                        i={_i}
                        {...state}
                        val={{
                          config: get(
                            val.find((f) => f.config),
                            'config'
                          ),
                          value,
                          count,
                        }}
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tab.rows.map((row, _i) => {
                  return (
                    <tr key={_i} className={classes.row}>
                      {row.map((r, __i) => (
                        <Cell key={__i} val={r} i={__i} {...state} getDisplayVal={getDisplayVal} />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ))}
      </div>
    </>
  );
};

const HeaderCell = (props) => {
  let { val, getHeaderTitle } = props;

  val = getHeaderTitle(props);

  const classes = useStyles();

  return (
    <td className={classes.col}>
      <div>
        <span>{get(val, 'value', get(val, 'name', typeof val === 'string' ? val : ''))}</span>
      </div>
    </td>
  );
};

export const Cell = (props) => {
  const { getDisplayVal, showCounts } = props;

  const { isLink, hasClick, scoreVal, display, slide } = getDisplayVal(props);

  const onClick = () => {
    if (isLink && hasClick) {
      hasClick();
    }
  };

  const classes = useStyles();
  const hasCount = get(props, 'val.count');
  const tooltip = get(props, 'val.tooltip');
  const displayCounts = showCounts && hasCount ? true : false;
  const bg = slide && !displayCounts ? getColorFromScore(scoreVal, slide) : '';

  return (
    <td
      onClick={onClick}
      title={tooltip || (hasCount ? `${hasCount} values` : undefined)}
      style={{ backgroundColor: bg }}
      className={clsx(classes.col, bg && classes.colHasBg, isLink && classes.rowLink)}
    >
      <span>{display}</span>
    </td>
  );
};
